import { useMemo, useState } from "react";

import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DuplicateSearchType } from "@/ContactDirectory/Models/enums/DuplicateSearchType";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { DirectoryPopupFooter } from "../Common/DirectoryPopupFooter";
import { FaxDirectorySearchResultItem } from "../Search/FaxDirectorySearchResultItem";
import { DuplicateItem } from "./DuplicateItem";

import styles from "./DuplicateWarningPopup.module.scss";

interface IDuplicateWarningPopupProps {
    onClose: () => void;
    duplicateList: FaxDirectorySearchResultItem[];
    onModify?: (model: IDirectoryEntity) => void;
    onUsedInstead?: (model: IDirectoryEntity) => void;
    duplicateSearchType: DuplicateSearchType;
}

function useDuplicateWarningMessage(searchType: DuplicateSearchType, hasOnlyOneDuplicate: boolean): string {
    const { getText } = useTranslation();
    return useMemo(
        () => {
            switch (searchType) {
                case DuplicateSearchType.Name:
                    return hasOnlyOneDuplicate
                        ? getText("ContactDirectory_DuplicateWarningMessage_Name_Single")
                        : getText("ContactDirectory_DuplicateWarningMessage_Name_Multiple");
                case DuplicateSearchType.Address:
                    return hasOnlyOneDuplicate
                        ? getText("ContactDirectory_DuplicateWarningMessage_Address_Single")
                        : getText("ContactDirectory_DuplicateWarningMessage_Address_Multiple");
                case DuplicateSearchType.FaxNumber:
                    return hasOnlyOneDuplicate
                        ? getText("ContactDirectory_DuplicateWarningMessage_FaxNumber_Single")
                        : getText("ContactDirectory_DuplicateWarningMessage_FaxNumber_Multiple");
                case DuplicateSearchType.Pharmacist:
                    return hasOnlyOneDuplicate
                        ? getText("ContactDirectory_DuplicateWarningMessage_Pharmacists_Single")
                        : getText("ContactDirectory_DuplicateWarningMessage_Pharmacists_Multiple");
                case DuplicateSearchType.LicenceNumber:
                    return hasOnlyOneDuplicate
                        ? getText("ContactDirectory_DuplicateWarningMessage_LicenseNumber_Single")
                        : getText("ContactDirectory_DuplicateWarningMessage_LicenseNumber_Multiple");
            }
        },
        [searchType]
    );
}

function DuplicateWarningPopup(props: IDuplicateWarningPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const hasOnlyOneDuplicate = props.duplicateList?.length === 1;

    const [selectedEntity, setSelectedEntity] = useState<FaxDirectorySearchResultItem>(hasOnlyOneDuplicate ? props.duplicateList[0] : null);

    const message = useDuplicateWarningMessage(props.duplicateSearchType, hasOnlyOneDuplicate);

    const faxDirectoryDuplicateList = useMemo(() => props.duplicateList.filter(p => p?.entity), [props.duplicateList]);

    return (
        <Modal
            id="DuplicatWarningPopup"
            title={getText("DuplicateWarningLabel")}
            onClose={() => props.onClose()}
            width={500}
            showOverlay
            fixedOverlayPosition
            disableOverlayClose
            centerContent
            top={10}
            footer={(
                <DirectoryPopupFooter
                    isValid={!!selectedEntity}
                    onCancel={() => props.onClose()}
                    cancelLabel="IgnoreLabel"
                    onModify={() => props.onModify?.(selectedEntity?.entity)}
                    onUseInstead={() => props.onUsedInstead?.(selectedEntity?.entity)}
                />
            )}
        >
            <p className="margin-md-bottom">{message}</p>
            <p>
                {hasOnlyOneDuplicate
                    ? getText("DuplicateWarningChoiceTitleLabel")
                    : getText("DuplicatesWarningChoiceTitleLabel")
                }
            </p>
            <ul>
                <li className={styles.choiceList}>
                    {hasOnlyOneDuplicate
                        ? getText("DuplicateWarningUseInsteadLabel")
                        : getText("DuplicatesWarningUseInsteadLabel")
                    }
                </li>
                <li className={styles.choiceList}>
                    {hasOnlyOneDuplicate
                        ? getText("DuplicateWarningModifyLabel")
                        : getText("DuplicatesWarningModifyLabel")
                    }
                </li>
            </ul>
            <div className={styles.listContainer}>
                <ul>
                    {faxDirectoryDuplicateList.map(item => (
                        <DuplicateItem
                            key={`entity${item.id}`}
                            item={item}
                            isSelected={selectedEntity?.entity?.id === item.id}
                            onSelect={() => setSelectedEntity(item)}
                        />
                    ))}
                </ul>
                <div className={styles.returnMessage}>
                    <p>
                        {getText("DuplicateWarningCancelLabel")}
                    </p>
                </div>
            </div>
        </Modal>
    );
}

export { DuplicateWarningPopup, IDuplicateWarningPopupProps };
